import { Mail, Phone, MapPin, UserRound, FileCheck2 } from "lucide-react";
import { IMAGES, SITE } from "@/config/site";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Photo } from "@/components/site/Photo";
import { Reveal, SectionHeading, Pending, Val, telHref } from "@/components/site/Primitives";
import { FormBuilder } from "@/components/forms/FormBuilder";
import { CONTACT_FIELDS } from "@/components/forms/formDefs";
import { LEADERSHIP } from "@/content/content";

const VALUES = [
  { t: "Service", d: "We exist because of the people who serve, and we measure our work by how well we serve them in return." },
  { t: "Integrity", d: "We represent our programs accurately, protect personal information, and use every contribution responsibly." },
  { t: "Compassion", d: "We approach every person and every dog with patience, dignity, and care." },
  { t: "Community", d: "We build partnerships with agencies, businesses, and neighbors who share our commitment." },
];

const TRANSPARENCY = [
  { t: "Verified nonprofit status", v: SITE.nonprofit.status.replace(/^an? /, "") },
  { t: "Employer Identification Number (EIN)", v: SITE.nonprofit.ein },
  { t: "Tax-deductibility disclosure", v: SITE.nonprofit.taxDeductibility },
  { t: "Florida charitable registration", v: SITE.nonprofit.stateRegistration && `Registration No. ${SITE.nonprofit.stateRegistration}` },
  { t: "Annual reports", v: "" },
  { t: "Financial transparency documents", v: "" },
];

const Leadership = () => (
  <section className="bg-mist py-24 md:py-32" data-testid="about-leadership-section">
    <div className="container-x">
      <SectionHeading eyebrow="Leadership" title="The People Behind the Mission." />
      <div className="mt-14 grid gap-5 md:grid-cols-3">
        {LEADERSHIP.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.08} className="bg-paper" data-testid={`leader-card-${i + 1}`}>
            {p.photo ? (
              <img src={p.photo} alt={`${p.name}, ${p.title}`} className="aspect-[4/5] w-full object-cover" style={{ objectPosition: p.position || "center" }} loading="lazy" data-testid={`leader-photo-${i + 1}`} />
            ) : (
              <div className="flex aspect-[4/5] flex-col items-center justify-center gap-3 border-b border-navy/10 bg-white" data-testid={`leader-photo-coming-soon-${i + 1}`}>
                <UserRound className="text-navy/20" size={72} />
                <p className="font-display text-sm font-bold text-navy/60">Photo Coming Soon</p>
              </div>
            )}
            <div className="p-7">
              <h3 className="text-xl font-bold">{p.name}</h3>
              <p className="mt-1 text-sm font-semibold text-gold-deep">{p.title}</p>
              <p className="mt-4 text-sm leading-relaxed text-ink/80">{p.bio}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Transparency = () => (
  <section className="py-24 md:py-32" data-testid="about-transparency-section">
    <div className="container-x grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
      <SectionHeading eyebrow="Transparency" title="Accountable to Those We Serve." intro="We believe donors and partners deserve clear information. Official disclosures and documents will be posted here as soon as they are verified." />
      <ul className="border-t border-navy/15">
        {TRANSPARENCY.map((t, i) => (
          <li key={t.t} className="flex flex-col gap-2 border-b border-navy/15 py-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`transparency-item-${i + 1}`}>
            <span className="flex items-center gap-3 font-semibold text-navy"><FileCheck2 size={18} className="text-gold-deep" />{t.t}</span>
            <Val value={t.v} label="to be provided" />
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="border-t border-navy/10 bg-white py-24 md:py-32" data-testid="about-contact-section">
    <div className="container-x grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
      <div>
        <SectionHeading eyebrow="Contact" title="We Would Like to Hear From You." intro="Questions about our programs, partnerships, or ways to help are always welcome." />
        <ul className="mt-10 space-y-5 text-ink">
          <li className="flex items-start gap-4"><Mail className="mt-0.5 text-gold-deep" size={20} /><Val value={SITE.contact.email} href={`mailto:${SITE.contact.email}`} label="Foundation email" /></li>
          <li className="flex items-start gap-4"><Phone className="mt-0.5 text-gold-deep" size={20} /><Val value={SITE.contact.phone} href={telHref(SITE.contact.phone)} label="Foundation phone" /></li>
          <li className="flex items-start gap-4"><MapPin className="mt-0.5 text-gold-deep" size={20} /><Val value={SITE.contact.mailingAddress} label="Mailing address" /></li>
        </ul>
        <p className="mt-10 border-l-2 border-fire pl-4 text-sm leading-relaxed text-ink/80">If you or someone you know is in crisis, call or text 988 to reach the 988 Suicide &amp; Crisis Lifeline. Veterans can press 1 after dialing. In an emergency, call 911.</p>
      </div>
      <Reveal delay={0.1} className="border border-navy/10 bg-paper p-6 md:p-10">
        <FormBuilder formType="contact" fields={CONTACT_FIELDS} testId="contact-form" submitLabel="Send Message" successTitle="Thank you. Your message has been sent." successText="A member of the Foundation team will respond as soon as possible." />
      </Reveal>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <Seo title="About & Contact | First Responders Foundation, Palm Beach County" description="Learn about First Responders Foundation, our commitment to first responders and veterans, our leadership, transparency, and how to contact us." path="/about" />
      <PageHero eyebrow="About & Contact" lines={[{ text: "Serving Those" }, { text: "Who Serve", className: "text-gold" }, { text: "South Florida." }]} intro="First Responders Foundation is a South Florida organization dedicated to supporting firefighters, law enforcement officers, emergency medical personnel, dispatchers, military veterans, and other public safety professionals." image={IMAGES.fireScene} />
      <section className="py-24 md:py-32" data-testid="about-overview-section">
        <div className="container-x grid items-center gap-14 lg:grid-cols-2 lg:gap-24">
          <div>
            <SectionHeading eyebrow="Who We Are" title="A Community Standing Behind Its Responders." />
            <Reveal delay={0.1} className="mt-6 space-y-5 text-base leading-relaxed text-ink/85 md:text-lg">
              <p>Those who serve our communities give a great deal of themselves. The Foundation was created to give something back: programs that promote connection, resilience, and well-being for the people who answer the call.</p>
              <p>Our featured Service Dog Program supports eligible first responders and veterans through carefully evaluated, appropriately trained dogs. Through Canteen 33, we assist requesting agencies with mobile canteen and rehabilitation support during extended incidents and training events.</p>
              <p>Luna, our ambassador dog, represents the Foundation in the community and reminds us why this work matters.</p>
            </Reveal>
          </div>
          <div className="flex justify-center bg-navy p-10 md:p-16">
            <img src={IMAGES.logo.src} alt={IMAGES.logo.alt} className="w-full max-w-sm" loading="lazy" data-testid="about-logo" />
          </div>
        </div>
        <div className="container-x mt-20 grid gap-px bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={i * 0.06} className="bg-paper p-8">
              <p className="font-display text-sm font-bold text-gold-deep">0{i + 1}</p>
              <h3 className="mt-4 text-xl font-bold">{v.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink/75">{v.d}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <Leadership />
      <Transparency />
      <Contact />
    </>
  );
}
